import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CircleMemberResponse } from "@consistency-circle/shared";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiClient } from "../../../lib/api";
import { circlesQueryKey } from "../hooks/useCircleData";

interface MemberRowProps {
  readonly canRemove: boolean;
  readonly circleId: string;
  readonly member: CircleMemberResponse;
}

export function MemberRow({ canRemove, circleId, member }: MemberRowProps) {
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => apiClient.removeCircleMember(circleId, member.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["circle-members", circleId] });
      void queryClient.invalidateQueries({ queryKey: circlesQueryKey });
    },
  });

  return (
    <div className="flex items-center gap-3 px-4 py-3 text-sm">
      <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-accent-foreground font-medium text-xs shrink-0">
        {member.displayName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{member.displayName}</div>
        <div className="text-xs text-muted-foreground truncate">{member.email}</div>
      </div>
      <Badge variant={member.role === "OWNER" ? "default" : "secondary"} className="text-xs shrink-0">
        {member.role.toLowerCase()}
      </Badge>
      {canRemove && (
        <Button
          variant="ghost"
          size="xs"
          className="text-muted-foreground hover:text-destructive shrink-0"
          onClick={() => {
            if (window.confirm(`Remove ${member.displayName} from this circle?`)) {
              remove.mutate();
            }
          }}
          disabled={remove.isPending}
        >
          {remove.isPending ? "Removing..." : "Remove"}
        </Button>
      )}
    </div>
  );
}
